import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { makeStyles } from '@mui/styles';
import { Paper, Grid, Box, Button, Avatar, Typography, Link, Tooltip, IconButton, Chip } from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { useAuthStore } from '../store/authStore';
import { urlClients, urlAccountants } from '../config';
import Controls from '../components/controls/Controls';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { logActionType } from "../services/MyCollections";
import { logActivity } from '../services/ApiServices';
import * as MyCollections from "../services/MyCollections";
import { fetchData, fetchDataPromise } from '../services/ApiServices';
import FormDialog from '../components/ClientCard';
import { useThemeContext } from '../components/contexts/ThemeContext';
import CustomTooltip from '../components/CustomTooltip';

const useStyles = makeStyles(theme => ({
  pageContent: {
    marginTop: 20
  },
}))

const initialValue = MyCollections.clientCardInitialValues;

const ClientCellRenderer = (props) => {
  const { data, darkTheme, onUpdate } = props;
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedNip, setCopiedNip] = useState(false);

  if (!data) return null;

  const companyWorkingName = data.companyWorkingName || '';
  const nip = data.nip || null;
  const rawRep = data.clientContactDTOs?.[0]?.name;
  const representative = rawRep && rawRep !== 'null null' ? rawRep : null;
  const phone = data.clientContactDTOs?.[0]?.phones?.[0]?.number || null;
  const url = data.clientPanelUrl;

  const getInitials = (name) => {
    if (!name) return 'K';
    const words = name.trim().split(/\s+/).filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleCopy = (e, text, setCopiedFn) => {
    e.stopPropagation();
    if (text) {
      navigator.clipboard.writeText(text);
      setCopiedFn(true);
      setTimeout(() => setCopiedFn(false), 2000);
    }
  };

  const nipColor = darkTheme ? '#c084fc' : '#8b5cf6';
  const textColor = darkTheme ? '#e2e8f0' : '#334155';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 1,
        px: 0.5,
        gap: 0.75,
        lineHeight: 1.3,
      }}
    >
      {/* Row 1: Avatar + Company Name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.75rem',
            fontWeight: 700,
            bgcolor: darkTheme ? 'rgba(56, 189, 248, 0.15)' : 'rgba(37, 99, 235, 0.1)',
            color: darkTheme ? '#38bdf8' : '#2563eb',
            border: `1px solid ${darkTheme ? 'rgba(56, 189, 248, 0.3)' : 'rgba(37, 99, 235, 0.25)'}`,
            flexShrink: 0,
            boxShadow: darkTheme ? '0 2px 4px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          {getInitials(companyWorkingName)}
        </Avatar>

        <Tooltip title="Kliknij, aby edytować kartę klienta" arrow placement="top">
          <Typography
            component="span"
            onClick={() => onUpdate && onUpdate(data)}
            sx={{
              fontWeight: 700,
              fontSize: '0.875rem',
              color: darkTheme ? '#60a5fa' : '#1d4ed8',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: darkTheme ? '#93c5fd' : '#2563eb',
                textDecoration: 'underline',
              },
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {companyWorkingName}
          </Typography>
        </Tooltip>
      </Box>

      {/* Row 2: NIP, Representative & Phone Details */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.4,
          pl: 1,
          borderLeft: `2px solid ${darkTheme ? 'rgba(148, 163, 184, 0.25)' : 'rgba(203, 213, 225, 0.7)'}`,
          ml: 0.75,
        }}
      >
        {/* NIP */}
        {nip && nip !== 'brak' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <span style={{ fontSize: '10px', fontWeight: 'bold', color: nipColor }}>NIP</span>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                color: textColor,
                fontFamily: 'monospace',
                fontWeight: 600,
              }}
            >
              {nip}
            </Typography>
            <Tooltip title={copiedNip ? "Skopiowano!" : "Kopiuj NIP"} arrow placement="right">
              <IconButton
                size="small"
                onClick={(e) => handleCopy(e, nip, setCopiedNip)}
                sx={{
                  p: 0.2,
                  ml: 0.2,
                  color: copiedNip ? (darkTheme ? '#4ade80' : '#16a34a') : (darkTheme ? '#64748b' : '#94a3b8'),
                  '&:hover': {
                    color: textColor,
                    backgroundColor: darkTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                {copiedNip ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 12 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Representative */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <PersonOutlinedIcon sx={{ fontSize: 14, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              color: representative ? (darkTheme ? '#e2e8f0' : '#334155') : (darkTheme ? '#64748b' : '#94a3b8'),
              fontStyle: representative ? 'normal' : 'italic',
              fontWeight: representative ? 500 : 400,
            }}
          >
            {representative || 'Brak reprezentanta'}
          </Typography>
        </Box>

        {/* Phone Number */}
        {phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PhoneOutlinedIcon sx={{ fontSize: 14, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
            <Link
              underline="hover"
              href={`tel:${phone}`}
              onClick={(e) => e.stopPropagation()}
              sx={{
                fontSize: '0.75rem',
                color: darkTheme ? '#38bdf8' : '#0284c7',
                fontWeight: 600,
                fontFamily: 'monospace, monospace',
                letterSpacing: '0.02em',
                '&:hover': { color: darkTheme ? '#7dd3fc' : '#0369a1' }
              }}
            >
              {phone}
            </Link>
            <Tooltip title={copiedPhone ? "Skopiowano!" : "Kopiuj telefon"} arrow placement="right">
              <IconButton
                size="small"
                onClick={(e) => handleCopy(e, phone, setCopiedPhone)}
                sx={{
                  p: 0.2,
                  ml: 0.2,
                  color: copiedPhone ? (darkTheme ? '#4ade80' : '#16a34a') : (darkTheme ? '#64748b' : '#94a3b8'),
                  '&:hover': {
                    color: textColor,
                    backgroundColor: darkTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                {copiedPhone ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 12 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Row 3: Action Link for Invoices */}
      {url && (
        <Box sx={{ pt: 0.2, pl: 0.75 }}>
          <Button
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            size="small"
            startIcon={<ReceiptLongOutlinedIcon sx={{ fontSize: '13px !important' }} />}
            endIcon={<OpenInNewIcon sx={{ fontSize: '11px !important', opacity: 0.7 }} />}
            sx={{
              py: 0.3,
              px: 1,
              minHeight: '24px',
              fontSize: '0.725rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              backgroundColor: darkTheme ? 'rgba(56, 189, 248, 0.1)' : 'rgba(2, 132, 199, 0.08)',
              color: darkTheme ? '#38bdf8' : '#0284c7',
              border: `1px solid ${darkTheme ? 'rgba(56, 189, 248, 0.25)' : 'rgba(2, 132, 199, 0.2)'}`,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: darkTheme ? 'rgba(56, 189, 248, 0.2)' : 'rgba(2, 132, 199, 0.15)',
                borderColor: darkTheme ? '#38bdf8' : '#0284c7',
                boxShadow: darkTheme ? '0 2px 8px rgba(56, 189, 248, 0.2)' : '0 2px 6px rgba(2, 132, 199, 0.15)',
              }
            }}
          >
            Faktury i panel
          </Button>
        </Box>
      )}
    </Box>
  );
};

const InvoicesCountRenderer = ({ value, darkTheme }) => {
  const count = value || 0;
  const label = `${count} ${count === 1 ? 'faktura' : count > 1 && count < 5 ? 'faktury' : 'faktur'}`;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Chip
        label={label}
        size="small"
        sx={{
          height: 22,
          fontSize: '0.725rem',
          fontWeight: 700,
          bgcolor: count > 0
            ? (darkTheme ? 'rgba(239, 68, 68, 0.18)' : 'rgba(220, 38, 38, 0.1)')
            : (darkTheme ? 'rgba(148, 163, 184, 0.15)' : 'rgba(100, 116, 139, 0.1)'),
          color: count > 0
            ? (darkTheme ? '#f87171' : '#dc2626')
            : (darkTheme ? '#94a3b8' : '#64748b'),
          border: `1px solid ${count > 0
            ? (darkTheme ? 'rgba(239, 68, 68, 0.35)' : 'rgba(220, 38, 38, 0.25)')
            : (darkTheme ? 'rgba(148, 163, 184, 0.25)' : 'rgba(100, 116, 139, 0.2)')}`,
          borderRadius: '6px',
        }}
      />
    </Box>
  );
};

const TotalDebtRenderer = ({ value, url, darkTheme }) => {
  const formatPLN = (val) => new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(val || 0);
  const formatted = formatPLN(value);

  const style = {
    fontWeight: 700,
    fontSize: '0.8rem',
    color: darkTheme ? '#f87171' : '#dc2626',
    fontFamily: 'monospace',
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      {url ? (
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          onClick={(e) => e.stopPropagation()}
          sx={style}
        >
          {formatted}
        </Link>
      ) : (
        <Typography variant="caption" sx={style}>
          {formatted}
        </Typography>
      )}
    </Box>
  );
};

const PaidAmountRenderer = ({ value, url, darkTheme }) => {
  const formatPLN = (val) => new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(val || 0);
  const formatted = formatPLN(value);

  const style = {
    fontWeight: 700,
    fontSize: '0.8rem',
    color: darkTheme ? '#4ade80' : '#16a34a',
    fontFamily: 'monospace',
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      {url ? (
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          onClick={(e) => e.stopPropagation()}
          sx={style}
        >
          {formatted}
        </Link>
      ) : (
        <Typography variant="caption" sx={style}>
          {formatted}
        </Typography>
      )}
    </Box>
  );
};

const CooperationStatusRenderer = (props) => {
  const { darkTheme } = props;
  const status = props.value;

  if (!status) return null;

  let color = darkTheme ? '#94a3b8' : '#64748b';
  let bg = darkTheme ? 'rgba(148, 163, 184, 0.12)' : 'rgba(100, 116, 139, 0.08)';
  let border = darkTheme ? 'rgba(148, 163, 184, 0.25)' : 'rgba(100, 116, 139, 0.2)';

  if (status === 'Aktywna') {
    color = darkTheme ? '#34d399' : '#059669';
    bg = darkTheme ? 'rgba(52, 211, 153, 0.15)' : 'rgba(5, 150, 105, 0.1)';
    border = darkTheme ? 'rgba(52, 211, 153, 0.3)' : 'rgba(5, 150, 105, 0.25)';
  } else if (status === 'Zawieszona') {
    color = darkTheme ? '#fbbf24' : '#d97706';
    bg = darkTheme ? 'rgba(251, 191, 36, 0.15)' : 'rgba(217, 119, 6, 0.1)';
    border = darkTheme ? 'rgba(251, 191, 36, 0.3)' : 'rgba(217, 119, 6, 0.25)';
  } else if (status === 'Zakończona') {
    color = darkTheme ? '#f87171' : '#dc2626';
    bg = darkTheme ? 'rgba(248, 113, 113, 0.15)' : 'rgba(220, 38, 38, 0.1)';
    border = darkTheme ? 'rgba(248, 113, 113, 0.3)' : 'rgba(220, 38, 38, 0.25)';
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Chip
        label={status}
        size="small"
        sx={{
          height: 22,
          fontSize: '0.725rem',
          fontWeight: 700,
          bgcolor: bg,
          color: color,
          border: `1px solid ${border}`,
          borderRadius: '6px',
        }}
      />
    </Box>
  );
};

const DateBadgeRenderer = (props) => {
  const { value, darkTheme } = props;

  if (!value) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.45 }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: darkTheme ? '#94a3b8' : '#64748b' }}>
          wstaw datę
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.8,
          py: 0.25,
          borderRadius: '6px',
          backgroundColor: darkTheme ? 'rgba(52, 211, 153, 0.15)' : 'rgba(16, 185, 129, 0.1)',
          color: darkTheme ? '#34d399' : '#059669',
          border: `1px solid ${darkTheme ? 'rgba(52, 211, 153, 0.3)' : 'rgba(16, 185, 129, 0.25)'}`,
        }}
      >
        <CalendarTodayIcon sx={{ fontSize: 11 }} />
        <Typography
          component="span"
          sx={{
            fontSize: '0.725rem',
            fontWeight: 700,
            fontFamily: 'monospace',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const AmountBadgeRenderer = (props) => {
  const { value, darkTheme } = props;

  if (!value && value !== 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.45 }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: darkTheme ? '#94a3b8' : '#64748b' }}>
          wprowadź kwotę
        </Typography>
      </Box>
    );
  }

  const formattedValue = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(value);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.8,
          py: 0.25,
          borderRadius: '6px',
          backgroundColor: darkTheme ? 'rgba(248, 113, 113, 0.15)' : 'rgba(220, 38, 38, 0.08)',
          color: darkTheme ? '#f87171' : '#dc2626',
          border: `1px solid ${darkTheme ? 'rgba(248, 113, 113, 0.3)' : 'rgba(220, 38, 38, 0.25)'}`,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 700,
            fontFamily: 'monospace',
          }}
        >
          {formattedValue}
        </Typography>
      </Box>
    </Box>
  );
};

const CommentRenderer = (props) => {
  const { value, darkTheme } = props;

  if (!value) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5, width: '100%' }}>
        <Typography variant="caption" sx={{ fontSize: '0.725rem', fontStyle: 'italic', color: darkTheme ? '#94a3b8' : '#64748b' }}>
          + dodaj tekst...
        </Typography>
      </Box>
    );
  }

  const maxLength = 65;
  const truncated = value.length > maxLength ? value.substring(0, maxLength) + '...' : value;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 0.5, height: '100%', width: '100%' }}>
      <ChatBubbleOutlineIcon sx={{ fontSize: 13, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.725rem',
          color: darkTheme ? '#cbd5e1' : '#334155',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.3,
          textAlign: 'center',
        }}
      >
        {truncated}
      </Typography>
    </Box>
  );
};

function DebtCollection() {
  const gridRef = useRef();
  const { roles: roles } = useAuthStore();

  const token = useAuthStore().token;
  const userName = useAuthStore().fullName;
  const [gridApi, setGridApi] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [client, setClient] = useState({});
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState((JSON.parse(JSON.stringify(initialValue))))
  const classes = useStyles();
  const today = new Date();
  const defaultFrom = new Date();
  defaultFrom.setDate(today.getDate() - 45);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { mode, toggleTheme } = useThemeContext();
  const darkTheme = mode === 'dark';

  const defaultTo = new Date();
  defaultTo.setDate(today.getDate());

  const [dateRange, setDateRange] = useState({
    from: defaultFrom.toISOString().slice(0, 10),
    to: defaultTo.toISOString().slice(0, 10)
  });

  const handleUpdate = useCallback(async (oldData) => {
    try {
      const clientData = await fetchDataPromise(urlClients + "/" + oldData.id, null, setClient, console.error, token);
      setFormData(clientData ? clientData : initialValue);
      setOpen(true);
    } catch (error) {
      console.error('Błąd podczas aktualizacji:', error);
    }
  }, [token]);

  const columnDefs = useMemo(() => [
    {
      headerName: "Klient",
      field: "companyWorkingName",
      lockPosition: 'left',
      cellStyle: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        textAlign: 'left',
      },
      minWidth: 230,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellRenderer: (params) => (
        <ClientCellRenderer
          data={params.data}
          darkTheme={darkTheme}
          onUpdate={handleUpdate}
        />
      )
    },
    {
      headerName: "Status Współpracy",
      field: "isActive",
      lockPosition: 'left',
      minWidth: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      valueGetter: function (params) {
        if (params.data.isActive) {
          return "Aktywna";
        } else if (params.data.cooperationHistoryDTO?.suspended) {
          return "Zawieszona";
        } else if (params.data.cooperationHistoryDTO?.cooperationEndDate) {
          return "Zakończona";
        } else {
          return "";
        }
      },
      cellRenderer: (params) => (
        <CooperationStatusRenderer
          value={params.value}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Faktury",
      field: "clientDebtStatusDTO.numberOfInvoices",
      lockPosition: 'left',
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      minWidth: 105,
      sortable: true,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      cellRenderer: (params) => (
        <InvoicesCountRenderer
          value={params.value}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Zaległość",
      field: "clientDebtStatusDTO.totalDebt",
      lockPosition: 'left',
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      minWidth: 120,
      sortable: true,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      cellRenderer: (params) => (
        <TotalDebtRenderer
          value={params.value}
          url={params.data?.clientPanelUrl}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Odzyskano",
      field: "clientDebtStatusDTO.paidAmount",
      lockPosition: 'left',
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      minWidth: 120,
      sortable: true,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      cellRenderer: (params) => (
        <PaidAmountRenderer
          value={params.value}
          url={params.data?.clientPanelUrl}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Kontakt tel.",
      field: "clientDebtStatusDTO.phoneContactAttemptDate",
      editable: true,
      minWidth: 120,
      lockPosition: 'left',
      cellEditor: 'agDateStringCellEditor',
      cellRenderer: (params) => (
        <DateBadgeRenderer
          value={params.value}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Wezwanie BIG",
      children: [
        {
          headerName: "Data",
          field: "clientDebtStatusDTO.bigSummonsSentDate",
          lockPosition: 'left',
          minWidth: 110,
          editable: true,
          cellEditor: 'agDateStringCellEditor',
          cellRenderer: (params) => (
            <DateBadgeRenderer
              value={params.value}
              darkTheme={darkTheme}
            />
          )
        },
        {
          headerName: "Kwota",
          field: "clientDebtStatusDTO.bigSummonsAmount",
          lockPosition: 'left',
          minWidth: 120,
          editable: true,
          valueParser: function (params) {
            const value = params.newValue;
            if (typeof value === 'string') {
              const normalizedValue = value.replace(',', '.');
              const parsedValue = parseFloat(normalizedValue);
              return isNaN(parsedValue) ? null : parsedValue;
            }
            return value;
          },
          filter: 'agNumberColumnFilter',
          cellRenderer: (params) => (
            <AmountBadgeRenderer
              value={params.value}
              darkTheme={darkTheme}
            />
          )
        },
      ],
      marryChildren: true,
    },
    {
      headerName: "Wpis w Inf. Gosp.",
      children: [
        {
          headerName: "Data",
          field: "clientDebtStatusDTO.negativeEconomicInfoAddedDate",
          lockPosition: 'left',
          minWidth: 110,
          editable: true,
          cellEditor: 'agDateStringCellEditor',
          cellRenderer: (params) => (
            <DateBadgeRenderer
              value={params.value}
              darkTheme={darkTheme}
            />
          )
        },
        {
          headerName: "Kwota",
          field: "clientDebtStatusDTO.negativeEconomicInfoAmount",
          lockPosition: 'left',
          minWidth: 120,
          editable: true,
          valueParser: function (params) {
            const value = params.newValue;
            if (typeof value === 'string') {
              const normalizedValue = value.replace(',', '.');
              const parsedValue = parseFloat(normalizedValue);
              return isNaN(parsedValue) ? null : parsedValue;
            }
            return value;
          },
          filter: 'agNumberColumnFilter',
          cellRenderer: (params) => (
            <AmountBadgeRenderer
              value={params.value}
              darkTheme={darkTheme}
            />
          )
        },
      ],
      marryChildren: true,
    },
    {
      headerName: "Giełda długów",
      field: "clientDebtStatusDTO.addedToDebtExchangeDate",
      editable: true,
      cellEditor: 'agDateStringCellEditor',
      minWidth: 120,
      lockPosition: 'left',
      cellRenderer: (params) => (
        <DateBadgeRenderer
          value={params.value}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Przygotowanie pozwu",
      field: "clientDebtStatusDTO.lawSuitReadyDate",
      editable: true,
      cellEditor: 'agDateStringCellEditor',
      minWidth: 135,
      lockPosition: 'left',
      cellRenderer: (params) => (
        <DateBadgeRenderer
          value={params.value}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Wysłanie pozwu",
      field: "clientDebtStatusDTO.lawSuitSentDate",
      editable: true,
      cellEditor: 'agDateStringCellEditor',
      minWidth: 125,
      lockPosition: 'left',
      cellRenderer: (params) => (
        <DateBadgeRenderer
          value={params.value}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Nakaz zapłaty",
      field: "clientDebtStatusDTO.paymentOrderDate",
      editable: true,
      cellEditor: 'agDateStringCellEditor',
      minWidth: 125,
      lockPosition: 'left',
      cellRenderer: (params) => (
        <DateBadgeRenderer
          value={params.value}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Komornik",
      field: "clientDebtStatusDTO.sentToTheBailiffDate",
      editable: true,
      cellEditor: 'agDateStringCellEditor',
      minWidth: 120,
      lockPosition: 'left',
      cellRenderer: (params) => (
        <DateBadgeRenderer
          value={params.value}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Wykreślenie z BIG",
      field: "clientDebtStatusDTO.negativeEconomicInfoRemoved",
      editable: true,
      cellEditor: 'agDateStringCellEditor',
      minWidth: 130,
      lockPosition: 'left',
      cellRenderer: (params) => (
        <DateBadgeRenderer
          value={params.value}
          darkTheme={darkTheme}
        />
      )
    },
    {
      headerName: "Link do panelu klienta",
      field: "clientPanelUrl",
      editable: false,
      hide: true
    },
    {
      headerName: "Reprezentant",
      field: "Reprezentant",
      valueGetter: function (params) {
        const representative = params.data.clientContactDTOs?.[0]?.name === 'null null' ? "brak..." : params.data.clientContactDTOs?.[0]?.name;
        return representative;
      },
      editable: false,
      hide: true
    },
    {
      headerName: "Komentarz",
      field: "clientDebtStatusDTO.comment",
      tooltipValueGetter: (params) => params.data?.clientDebtStatusDTO?.comment,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      editable: true,
      filter: 'agTextColumnFilter',
      cellEditorParams: { maxLength: 1990 },
      minWidth: 160,
      lockPosition: 'left',
      headerClass: 'no-separator',
      cellRenderer: (params) => (
        <CommentRenderer
          value={params.value}
          darkTheme={darkTheme}
        />
      )
    },
  ], [darkTheme, handleUpdate]);

  useEffect(() => {
    handleShow()
  }, []);

  const onGridReady = (params) => {
    setGridApi(params)
  }

  const defaultColDef = {
    sortable: true,
    resizable: true,
    editable: false,
    floatingFilter: false,
    filter: false,
    autoHeight: true,
    tooltipComponent: CustomTooltip,
    flex: 1,
    headerClass: 'ag-center-header',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  }

  const handleDateChange = (name, newValue) => {
    setDateRange({
      ...dateRange,
      [name]: newValue,
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onChange = (e) => {
    const { value, id } = e.target
    // console.log(value,id)
    setFormData({ ...formData, [id]: value })
  }

  const handleFormSubmit = () => {
    formData.editedBy = userName;
    const activityType = logActionType.CLIENT_CARD_EDIT;

    fetch(urlClients + `/${formData.id}`, {
      method: "PUT",
      body: JSON.stringify(formData),
      headers: {
        'Content-Type': "application/json",
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => {
        if (!response.ok) {
          if (response.status === 400) {
            throw new Error("Klient o podanym numerze NIP już istnieje w bazie");
          }
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return true;
      })
      .then(data => {
        logActivity(`${activityType} ${formData.companyWorkingName}, NIP: ${formData.nip}`, userName, token);
        handleClose();
      })
      .catch(error => {
        alert(error.message); // Display the error message to the user
        console.error('Error:', error);
      });
  };

  const isValidDate = (date) => {
    if (date !== null) {
      const dateObject = new Date(date);
      return dateObject instanceof Date && !isNaN(dateObject);
    }
    else {
      return false
    }
  };

  const handleShow = () => {
    // Pobierz daty from i to
    const from = dateRange.from;
    const to = dateRange.to;

    // Sprawdź, czy obie daty są poprawne
    if (!isValidDate(from) || !isValidDate(to)) {
      alert('Uzupełnij obie daty');
      return;
    }

    // Sprawdź, czy from nie jest późniejsze niż to
    if (from > to) {
      alert('Data Od nie może być późniejsza niż data Do.');
      return;
    }

    let url = urlClients + '/allClientsInDebt';
    const requestData = {
      from: from,
      to: to,
    };

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestData),
    })
      .then(response => response.json())
      .then(data => {
        setTableData(data);
      })
      .catch(error => {
        console.error('Błąd podczas wysyłania żądania:', error);
      });
  };

  const getRowId = useMemo(() => {
    return (params) => {
      return params.data.id;
    };
  }, []);

  const handleRowDataChange = ({ data, colDef }) => {
    const { nip, ...dataWithoutNip } = data;

    fetch(urlClients + `/debt/${data.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(dataWithoutNip),
    })
      .then((response) => response.json())
      .then((responseData) => {
        console.log('Aktualizowany rekord został wysłany do bazy danych:', responseData);
        logActivity(logActionType.DEBT_STATUS_ROW_EDIT + ", kolumna: '" + colDef.headerName + "', klient: " + responseData.companyWorkingName, userName, token);
      })
      .catch((error) => {
        alert('Błąd podczas wysyłania danych');
      });
  };

  const onBtnExport = useCallback(() => {
    const columnsToExport = ["companyWorkingName", "Reprezentant", "nip", "clientDebtStatusDTO.totalDebt", "clientPanelUrl"];

    gridRef.current.api.exportDataAsCsv({
      columnKeys: columnsToExport,
    });
  }, []);

  const clearFilters = useCallback(() => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;

    if (api) {
      api.setFilterModel(null);

      if (api.applyColumnState) {
        api.applyColumnState({ defaultState: { sort: null } });
      } else if (columnApi && columnApi.applyColumnState) {
        columnApi.applyColumnState({ defaultState: { sort: null } });
      } else if (api.setSortModel) {
        api.setSortModel(null);
      }
    }
  }, []);

  return (
    <>
      <Paper className={classes.pageContent} elevation={0}>
        <Box
          sx={{
            py: 1,
            px: 2,
            mb: 1.5,
            mx: 1,
            mt: 0.5,
            borderRadius: '10px',
            backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.85)',
            border: `1px solid ${darkTheme ? 'rgba(148, 163, 184, 0.15)' : 'rgba(226, 232, 240, 0.9)'}`,
            backdropFilter: 'blur(8px)',
            boxShadow: darkTheme ? '0 2px 10px rgba(0,0,0,0.15)' : '0 1px 4px rgba(0,0,0,0.02)',
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { xs: 'stretch', lg: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          {/* Lewa sekcja: Tytuł i Filtry Daty */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
            {/* Etykieta grupy filtrów */}
            <Box display="flex" alignItems="center" gap={0.75} sx={{ mr: 0.5 }}>
              <DateRangeIcon sx={{ color: darkTheme ? '#38bdf8' : '#0284c7', fontSize: 18 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  color: darkTheme ? '#e2e8f0' : '#334155',
                  whiteSpace: 'nowrap',
                }}
              >
                Zakres dat:
              </Typography>
            </Box>

            {/* Pola wyboru dat */}
            <Box display="flex" alignItems="center" gap={1}>
              <Box width={155}>
                <Controls.MyDatePicker
                  name="from"
                  value={dateRange.from}
                  label="Data od"
                  onChange={newValue => handleDateChange('from', newValue)}
                  format={"YYYY-MM-DD"}
                  size="small"
                />
              </Box>

              <Typography variant="body2" sx={{ color: darkTheme ? '#64748b' : '#94a3b8', fontWeight: 600 }}>
                —
              </Typography>

              <Box width={155}>
                <Controls.MyDatePicker
                  name="to"
                  value={dateRange.to}
                  label="Data do"
                  onChange={newValue => handleDateChange('to', newValue)}
                  format={"YYYY-MM-DD"}
                  size="small"
                />
              </Box>

              <Controls.PremiumButton
                variant="green"
                size="small"
                onClick={handleShow}
                label="Filtruj"
                icon="fas fa-search"
                sx={{ minWidth: '85px', height: '32px', textTransform: 'none', fontWeight: 600 }}
              />
            </Box>
          </Box>

          {/* Prawa sekcja: Przyciski akcji */}
          <Box display="flex" alignItems="center" justifyContent={{ xs: 'space-between', lg: 'flex-end' }} flexWrap="wrap" gap={1}>
            <Controls.PremiumButton
              variant="glass"
              label="Wyczyść"
              onClick={clearFilters}
              icon="fas fa-filter"
            />

            <Controls.PremiumButton
              variant="glass"
              label="CSV"
              onClick={onBtnExport}
              icon="fas fa-download"
            />
          </Box>
        </Box>
        <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }}>
          <Grid item xs={12}>
            <div className="App1">
              <div className="tableData">
                {/* On div wrapping Grid a) specify theme CSS Class Class and b) sets Grid size */}
                <div
                  className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                  style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                >
                  <AgGridReact
                    rowData={tableData}
                    ref={gridRef}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    suppressBrowserResizeObserver={true}
                    onCellValueChanged={handleRowDataChange}
                    animateRows={true}
                    rowSelection={false}
                    enableBrowserTooltips={false}
                    suppressSizeToFit={true}
                    headerHeight={34}
                    localeText={MyCollections.localeTextPL}
                    tooltipShowDelay={0}
                    popupParent={document.body}
                    pagination={true}
                    paginationPageSize={25}
                    overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                    overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                  />
                </div>
              </div>
            </div>
            <FormDialog open={open} handleClose={() => handleClose()}
              onChange={onChange} handleFormSubmit={handleFormSubmit} setFormData={setFormData} formData={formData} />
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}

export default DebtCollection;